import { useEffect, useState } from "react";
import { Search, Loader2, ShieldX, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, imgSrc } from "@/lib/api";

const when = (s) => new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

function Entry({ e }) {
  const [open, setOpen] = useState(false);
  const bad = !e.ok;
  return (
    <div className={`rounded-2xl border ${bad ? "border-red-200 bg-red-50/60" : "border-pink-100 bg-white"}`} data-testid={`adminlog-entry-${e.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-start gap-3 p-3 text-left">
        {e.admin_picture ? <img src={imgSrc(e.admin_picture)} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" /> : <span className="w-9 h-9 rounded-full bg-rose-100 text-rose-600 font-bold grid place-items-center shrink-0">{(e.admin_name || "?")[0]}</span>}
        <span className="min-w-0 flex-1">
          <span className="block text-sm"><b>{e.admin_name}</b> · <span className="text-slate-500">{when(e.created_at)}</span></span>
          <span className="block text-sm font-semibold text-slate-800 break-words">{e.label}</span>
          {e.target_name && <span className="block text-xs text-slate-500">Betrifft: <b>{e.target_name}</b></span>}
          {bad && <span className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-red-600"><ShieldX className="w-3.5 h-3.5" /> {e.blocked ? "Blockiert" : `Fehlgeschlagen (${e.status})`}</span>}
        </span>
        <ChevronDown className={`w-4 h-4 mt-1 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs space-y-1.5" data-testid={`adminlog-detail-${e.id}`}>
          <p className="font-mono break-all text-slate-600">{e.method} {e.path}{e.query ? `?${e.query}` : ""}</p>
          {e.data != null && <pre className="whitespace-pre-wrap break-all bg-slate-50 rounded-xl p-2.5 text-slate-700 max-h-64 overflow-auto">{typeof e.data === "string" ? e.data : JSON.stringify(e.data, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
}

export function AdminAuditLog() {
  const [admins, setAdmins] = useState([]);
  const [f, setF] = useState({ admin_id: "", q: "" });
  const [d, setD] = useState(null);
  const [more, setMore] = useState(false);
  useEffect(() => { api.get("/owner/admins").then(({ data }) => setAdmins(data.filter((a) => !a.is_owner))).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/owner/audit", { params: { admin_id: f.admin_id || undefined, q: f.q || undefined } }).then(({ data }) => setD(data)).catch((e) => toast.error(formatErr(e))), 300);
    return () => clearTimeout(t);
  }, [f]);
  const loadMore = async () => {
    setMore(true);
    try {
      const { data } = await api.get("/owner/audit", { params: { admin_id: f.admin_id || undefined, q: f.q || undefined, before: d.items[d.items.length - 1].created_at } });
      setD({ ...d, items: [...d.items, ...data.items], end: data.items.length < 60 });
    } catch (e) { toast.error(formatErr(e)); } finally { setMore(false); }
  };
  return (
    <div className="space-y-4" data-testid="admin-audit-log">
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={f.admin_id} onChange={(e) => setF({ ...f, admin_id: e.target.value })} data-testid="adminlog-filter-admin" className="h-11 rounded-full border border-pink-100 bg-white px-4 text-sm">
          <option value="">Alle Admins</option>
          {admins.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
        </select>
        <label className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Suchen, z. B. Punkte, Name, Spiele …" data-testid="adminlog-search"
            className="w-full h-11 pl-11 pr-4 rounded-full border border-pink-100 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
      </div>
      {!d ? <Loader2 className="w-5 h-5 animate-spin text-rose-400" /> : (
        <>
          <p className="text-xs text-slate-500" data-testid="adminlog-total">{d.total.toLocaleString("de-DE")} Einträge insgesamt</p>
          {d.items.length === 0 && <p className="text-sm text-slate-400 text-center py-8" data-testid="adminlog-empty">Noch keine Admin-Aktionen.</p>}
          <div className="space-y-2">{d.items.map((e) => <Entry key={e.id} e={e} />)}</div>
          {d.items.length >= 60 && !d.end && <button onClick={loadMore} disabled={more} data-testid="adminlog-more" className="w-full h-11 rounded-full bg-rose-50 text-rose-600 font-bold">{more ? "Lädt …" : "Ältere laden"}</button>}
        </>
      )}
    </div>
  );
}
